import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MessageCircle, ArrowRight, Camera, Calculator, FileCheck2, Globe, PieChart, ShieldCheck } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import WhatsAppDemo from '../components/WhatsAppDemo';
import { WHATSAPP_LINK } from '../utils/constants';
import RelatedLinks from '../components/RelatedLinks';
import PageGuide from '../components/PageGuide';

const STEPS = [
  { n: '1', title: 'Text your money on WhatsApp', desc: 'Send "invoiced $900" or "spent $60 on software" — MoneyFeed reads it instantly.' },
  { n: '2', title: 'AI does the accounting', desc: 'Every message is logged, categorized to the right account and mapped to your tax lines.' },
  { n: '3', title: 'See tax-ready books', desc: 'Live P&L, balance and Schedule C / VAT reports you can export as PDF, CSV or Excel.' },
];

const FEATURES = [
  { icon: Calculator, title: 'Automated bookkeeping', desc: 'Income, expenses and receipts are booked to the correct account with zero data entry.' },
  { icon: Camera, title: 'Receipt & invoice scanning', desc: 'Snap a receipt or forward an invoice and the amount, tax and vendor are captured for you.' },
  { icon: PieChart, title: 'Live P&L and reports', desc: 'A real profit & loss and tax summary that updates the moment you send a message.' },
  { icon: FileCheck2, title: 'Accountant-ready exports', desc: 'Schedule C, Self Assessment, T2125 and VAT reports — CPA-ready in one click.' },
  { icon: Globe, title: 'Multi-country & currency', desc: 'US, UK, Canada, UAE and Saudi Arabia tax rules, with multi-currency support.' },
  { icon: ShieldCheck, title: 'Secure & private', desc: 'Encrypted, private to your account, never sold. Export or delete anytime.' },
];

const FAQS = [
  { q: 'What is WhatsApp accounting?', a: 'WhatsApp accounting means doing your bookkeeping by simply messaging MoneyFeed on WhatsApp. You text your income and expenses in plain language and AI keeps a full, tax-ready set of books — no accounting software to learn.' },
  { q: 'Is MoneyFeed real accounting software?', a: 'Yes. Behind the chat, MoneyFeed keeps proper double-entry-style books: categorized transactions, a live profit & loss, and accountant-ready tax reports you can export to PDF, CSV or Excel.' },
  { q: 'Do I need an accountant?', a: 'MoneyFeed keeps your books clean and tax-ready all year, and you can hand your accountant a CPA-ready export whenever you file. Many freelancers use it to reduce or replace hourly bookkeeping work.' },
  { q: 'Which taxes and countries are supported?', a: 'The US (1099 / Schedule C), UK (Self Assessment), Canada (T2125) and VAT for the UAE and Saudi Arabia, all with multi-currency support.' },
  { q: 'Is there a free plan?', a: 'Yes, you can start free. Paid plans unlock unlimited transactions, bulk receipt scanning and advanced reports. There is no charge to try it.' },
];

export default function WhatsAppAccounting() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'WhatsApp Accounting for Freelancers',
      url: 'https://moneyfeed.io/whatsapp-accounting',
      description: 'AI accounting on WhatsApp. Text your income and expenses and get tax-ready books and reports for freelancers and small businesses.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#E0F2FE] to-[#D1FAE5]" data-testid="whatsapp-accounting-page">
      <Seo
        title="WhatsApp Accounting — AI Accounting on WhatsApp for Freelancers | MoneyFeed"
        description="WhatsApp accounting for freelancers & small businesses. Text income & expenses, snap receipts, and get tax-ready books and reports — no software to install. Free to start."
        path="/whatsapp-accounting"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-8 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#25D366]/10 rounded-full mb-5">
            <MessageCircle className="h-4 w-4 text-[#128C7E]" />
            <span className="text-xs font-bold text-[#128C7E] uppercase tracking-wider">Accounting on WhatsApp</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-4">
            AI accounting, right inside <span className="text-[#25D366]">WhatsApp</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-xl">
            Skip the accounting software. Text your income and expenses to MoneyFeed and its AI keeps proper, tax-ready books — a live P&L, categorized accounts and CPA-ready reports — all from the chat you already use. Just want the books done? See <Link to="/whatsapp-bookkeeping" className="text-[#128C7E] underline hover:text-[#064E3B]">WhatsApp bookkeeping</Link>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/auth">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="wa-get-started">
                Get started free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="wa-whatsapp">
                <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
              </Button>
            </a>
          </div>
        </div>
        <div className="flex justify-center" data-testid="wa-demo">
          <WhatsAppDemo />
        </div>
      </section>

      <PageGuide />

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">How WhatsApp accounting works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl shadow-md p-6" data-testid={`wa-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white font-bold flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="text-lg font-bold text-[#064E3B] mb-2">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Real accounting, done from a chat</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl shadow-md p-6 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                <f.icon className="h-5 w-5 text-[#064E3B]" />
              </div>
              <div>
                <h3 className="font-bold text-[#064E3B] mb-1">{f.title}</h3>
                <p className="text-gray-600 text-sm">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">WhatsApp accounting — FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`wa-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why WhatsApp accounting */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">Why WhatsApp accounting beats apps &amp; spreadsheets</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-md p-6" data-testid="wa-why-0">
            <h3 className="font-bold text-[#064E3B] mb-2">No new app to learn</h3>
            <p className="text-gray-600 text-sm">Spreadsheets get abandoned and full accounting apps are overkill for a freelancer. You already open WhatsApp dozens of times a day — so your books actually get done.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6" data-testid="wa-why-1">
            <h3 className="font-bold text-[#064E3B] mb-2">Done in the moment</h3>
            <p className="text-gray-600 text-sm">Log a coffee, a client payment or a receipt the second it happens — no "I'll enter it later" pile waiting for you at tax time.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6" data-testid="wa-why-2">
            <h3 className="font-bold text-[#064E3B] mb-2">Tax-ready all year</h3>
            <p className="text-gray-600 text-sm">Every message maps to the right tax line, so your Schedule C, Self Assessment or VAT return is ready the moment you need to file.</p>
          </div>
        </div>
        <p className="text-center text-gray-600 text-sm mt-8 max-w-2xl mx-auto">
          Prefer a calculator first? Try the free <Link to="/tools/1099-tax-calculator" className="text-[#064E3B] font-semibold underline">1099 tax calculator</Link>, scan a receipt with the <Link to="/receipt-to-excel" className="text-[#064E3B] font-semibold underline">receipt to Excel</Link> tool, or read the <Link to="/whatsapp-bookkeeping" className="text-[#064E3B] font-semibold underline">WhatsApp bookkeeping</Link> guide.
        </p>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Your books, done by text</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">No software, no spreadsheets. Send your first transaction on WhatsApp and watch MoneyFeed keep the books for you.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="wa-cta-signup">
                Get started free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/whatsapp-bookkeeping">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="wa-cta-bookkeeping">
                See WhatsApp bookkeeping
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <RelatedLinks exclude={['/whatsapp-accounting']} />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-bookkeeping" className="hover:text-[#064E3B]">WhatsApp Bookkeeping</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
